// Justificación de tamaño: el panel maneja muchos tabs (información de contacto, grupos en común, miembros, tareas, mensajes) y renderizado detallado. Refactor pendiente para extraer los tabs a componentes separados.
import React, { useState, useEffect, useRef } from 'react';
import { isGroupJid } from '../../../lib/sortChats';
import './UserProfilePanel.css';
import {
  Pin, Phone, Mail, Briefcase, Image as ImageIcon, Video, File, Download, Eye, ExternalLink, FolderOpen, LayoutDashboard, Info, CheckSquare, MessageSquare, Zap,
  Activity, FileText, BookOpen, ClipboardList, CalendarClock, MessageCircle, Heart,
  Plus, MoreVertical, X, Copy, Edit2, Check, Settings, MonitorPlay, Users, UserPlus, UserMinus
} from 'lucide-react';
import { uploadWhatsAvatar } from '../../../lib/whatsProfile';
import { ChatAvatar } from './ChatAvatar.jsx';
import WhatsLinkChatModal from './WhatsLinkChatModal';
import WhatsTaskCard from './WhatsTaskCard';
import WhatsTaskLinkSearch from './WhatsTaskLinkSearch';
import { useWhatsAppChatMedia } from '../../../../client/hooks/useWhatsAppData';
import { useContactByPhone, useUpdateContactByPhone } from '../../../hooks/useContactByPhone';
import ComponentUserSessions from '../../../../profile/components/ComponentUserSessions';
import { useLinkWhatsAppChat, useUpdateWhatsAppChatPriority, useWhatsAppCommonGroups, useWhatsAppGroupMembers, useCreateWhatsAppGroup, useUpdateWhatsAppGroupParticipants } from '../../../../client/hooks/useWhatsAppData';
import WhatsCreateGroupModal from './WhatsCreateGroupModal';
import WhatsAddParticipantsModal from './WhatsAddParticipantsModal';
// eslint-disable-next-line no-restricted-imports
import { useToast } from '../../../../../shared/ui/Toast/ToastProvider';

export function formatWhatsAppNumber(rawJidOrNumber) {
  if (!rawJidOrNumber) return '';
  const rawNumber = rawJidOrNumber.split('@')[0];
  
  // LID (WhatsApp Privacy hidden number) - 14 digits that do not look like a phone number
  if (rawJidOrNumber.endsWith('@lid') || rawNumber.length >= 14) {
    return 'Usuario Oculto';
  }

  // Número de México con el '1' adicional de celulares (ej. 52 1 33 1770 9424 -> 5213317709424)
  if (rawNumber.length === 13 && rawNumber.startsWith('521')) {
    const code = '52';
    const area = rawNumber.substring(3, 5); // Ej: 33
    const p1 = rawNumber.substring(5, 9); // Ej: 1770
    const p2 = rawNumber.substring(9); // Ej: 9424
    return `+${code} ${area} ${p1} ${p2}`;
  }
  
  // Número de México estándar de 12 dígitos (ej. 52 33 1770 9424 -> 523317709424)
  if (rawNumber.length === 12 && rawNumber.startsWith('52')) {
    const code = '52';
    const area = rawNumber.substring(2, 4);
    const p1 = rawNumber.substring(4, 8);
    const p2 = rawNumber.substring(8);
    return `+${code} ${area} ${p1} ${p2}`;
  }

  // Fallback genérico para otros países
  if (rawNumber.length >= 10) {
    return `+${rawNumber.substring(0, 2)} ${rawNumber.substring(2, 4)} ${rawNumber.substring(4, 8)} ${rawNumber.substring(8)}`;
  }
  
  return `+${rawNumber}`;
}

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: sincroniza el valor local editable con el prop `value` (patrón `EditableField`).
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const EditableField = ({ value, onSave, placeholder, type = "text" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (currentValue !== value) {
      onSave(currentValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setCurrentValue(value || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', flex: 1 }}>
        <input
          ref={inputRef}
          type={type}
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="up-edit-input"
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div 
      className={`up-editable-text ${!value ? 'empty' : ''}`} 
      onClick={() => setIsEditing(true)}
      title="Clic para editar"
    >
      {value || placeholder}
    </div>
  );
};

const UserProfilePanel = ({ chat, presences, messages = [], onClose, onPriorityChange, allChats = [], onNotify, onSelectChat, onOpenTask }) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('Settings');
  const [mediaFilter, setMediaFilter] = useState('photos'); // 'photos' | 'docs' | 'all'
  const [previewMedia, setPreviewMedia] = useState(null); // { url, name, type }
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState('all'); // 'all' | 'photos' | 'videos'
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadZip = (type) => {
    if (!chat?.chat_jid) return;
    setIsDownloading(true);
    const typeLabel = type === 'photos' ? 'fotos y videos' : type === 'docs' ? 'documentos' : 'archivos';
    if (toast?.info) {
      toast.info(`Iniciando descarga de ${typeLabel}...`);
    }

    const downloadUrl = `/api/whatsapp/chats/${encodeURIComponent(chat.chat_jid)}/media/download-zip?type=${type}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `whatsapp_${chat.chat_jid.split('@')[0]}_${type}.zip`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setIsDownloading(false);
    }, 2500);
  };

  const chatMediaQuery = useWhatsAppChatMedia(chat?.chat_jid);
  const mediaStats = chatMediaQuery.data?.stats || { imagesCount: 0, videosCount: 0, docsCount: 0, totalCount: 0 };
  const allMediaList = chatMediaQuery.data?.media || [];

  // Categorize media
  const photoMedia = React.useMemo(() => {
    return allMediaList.filter(m => m.media_type === 'image' || m.media_type === 'imageMessage' || m.media_type === 'video' || m.media_type === 'videoMessage');
  }, [allMediaList]);

  const docMedia = React.useMemo(() => {
    return allMediaList.filter(m => m.media_type === 'document' || m.media_type === 'documentMessage' || (!m.media_type?.includes('image') && !m.media_type?.includes('video') && !m.media_type?.includes('audio')));
  }, [allMediaList]);

  const displayedMedia = mediaFilter === 'photos' ? photoMedia : mediaFilter === 'docs' ? docMedia : allMediaList;
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [priority, setPriority] = useState(chat?.priority || 'baja');
  const [isTaskSearchOpen, setIsTaskSearchOpen] = useState(false);
  
  useEffect(() => {
    setPriority(chat?.priority || 'baja');
  }, [chat?.priority, chat?.chat_jid]);
  const fileInputRef = useRef(null);
  // Integración main→dev (catch-up F): vincular este chat con otro en la bandeja.
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const isGroup = isGroupJid(chat?.chat_jid);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const createGroupMutation = useCreateWhatsAppGroup();
  const updateParticipantsMutation = useUpdateWhatsAppGroupParticipants();

  const handleCreateGroup = async ({ subject, participants, avatarUrl }) => {
    try {
      const res = await createGroupMutation.mutateAsync({ subject, participants, avatarUrl });
      toast.success('¡Grupo creado con éxito!');
      if (res?.group && onSelectChat) {
        onSelectChat(res.group);
      }
    } catch (err) {
      toast.error(err.message || 'Error al crear el grupo');
      throw err;
    }
  };

  const handleAddParticipants = async (selectedJids) => {
    try {
      await updateParticipantsMutation.mutateAsync({
        jid: chat?.chat_jid,
        participants: selectedJids,
        action: 'add'
      });
      toast.success('Participantes añadidos al grupo');
      groupMembersQuery.refetch();
    } catch (err) {
      toast.error(err.message || 'Error al añadir participantes');
      throw err;
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    try {
      await updateParticipantsMutation.mutateAsync({
        jid: chat?.chat_jid,
        participants: [memberToRemove.member.jid],
        action: 'remove'
      });
      toast.success('Participante eliminado del grupo');
      setMemberToRemove(null);
      groupMembersQuery.refetch();
    } catch (err) {
      toast.error(err.message || 'Error al eliminar participante');
    }
  };
  const linkChatMutation = useLinkWhatsAppChat();
  const updatePriorityMutation = useUpdateWhatsAppChatPriority();
  const commonGroupsQuery = useWhatsAppCommonGroups(!isGroup ? chat?.chat_jid : null);
  const groupMembersQuery = useWhatsAppGroupMembers(isGroup ? chat?.chat_jid : null);

  // Format phone number
  const jid = chat?.chat_jid || '';
  const rawNumber = jid.split('@')[0];
  const formattedNumber = formatWhatsAppNumber(jid) || '+52 55 1234 5678';

  // 02.7-settings.md paso b -- reemplaza los 2 fetch() crudos por el hook construido en el paso a.
  const contactQuery = useContactByPhone(rawNumber);
  const contactData = contactQuery.data?.contact ?? null;
  const updateContactMutation = useUpdateContactByPhone(rawNumber);

  const handleUpdateContact = (updates, isName = false) => {
    if (!rawNumber) return;
    updateContactMutation.mutate(updates, {
      onSuccess: () => {
        if (isName) {
          toast.success('nombre de whats guardado');
          if (onNotify) onNotify('nombre de whats guardado');
        }
      }
    });
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(formattedNumber);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);
      const uploadedUrl = await uploadWhatsAvatar(file);
      if (uploadedUrl && typeof uploadedUrl === 'string') {
        updateContactMutation.mutate({ avatar_url: uploadedUrl }, {
          onSuccess: () => {
            toast.success('img de whats guardada');
            if (onNotify) onNotify('img de whats guardada');
          }
        });
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePriorityChange = (newPriority) => {
    if (chat?.chat_jid) {
      setPriority(newPriority);
      updatePriorityMutation.mutate({ jid: chat.chat_jid, priority: newPriority }, {
        onSuccess: () => {
          toast.success(`He modificado en la conversación de: ${chat.name || formattedNumber} su prioridad a: ${newPriority}`);
          if (onNotify) onNotify(`He modificado en la conversación de: ${chat.name || formattedNumber} su prioridad a: ${newPriority}`);
          onPriorityChange?.();
        },
        onError: () => {
          setPriority(chat.priority || 'baja');
        }
      });
    } else {
      setPriority(newPriority);
    }
  };

  const currentPriority = priority;

  const contactName = contactData?.name || chat?.name || 'Usuario de WhatsApp';
  
  // Get presence logic
  const presenceObj = presences?.[chat?.chat_jid] ? Object.values(presences[chat.chat_jid])[0] : null;
  const isOnline = presenceObj?.lastKnownPresence === "available";
  
  let lastSeenText = "";
  let isConnectedText = false;
  
  if (isOnline) {
    lastSeenText = "Conectado";
    isConnectedText = true;
  } else if (presenceObj?.lastSeen) {
    const dateObj = new Date(Number(presenceObj.lastSeen) * 1000);
    const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    lastSeenText = `Últ. vez a las ${timeStr}`;
  } else if (chat?.last_message_timestamp) {
    const dateObj = new Date(chat.last_message_timestamp);
    const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const isToday = new Date().toDateString() === dateObj.toDateString();
    if (isToday) {
      lastSeenText = `Últ. vez hoy a las ${timeStr}`;
    } else {
      const dateStr = dateObj.toLocaleDateString([], { day: '2-digit', month: 'short' });
      lastSeenText = `Últ. vez el ${dateStr} a las ${timeStr}`;
    }
  }
  const companyName = contactData?.company || '';
  const emailAddr = contactData?.emails?.[0] || '';
  const tags = contactData?.tags || [];

  const handleAddTag = () => {
    // TODO: reemplazar por un modal HTML (patrón LinkPromptModal.jsx) — deuda registrada
    // en la auditoría de paquetes 2026-09-02, no se tocó para no ampliar el alcance del deploy.
    // eslint-disable-next-line no-restricted-globals
    const newTag = prompt("Ingresa la nueva etiqueta:");
    if (newTag && newTag.trim()) {
      handleUpdateContact({ tags: [...tags, newTag.trim()] });
    }
  };
  
  const tabs = [
    { id: 'Settings', label: 'Settings', icon: <Settings size={16} /> },
    { id: 'Sessions', label: 'Sessions', icon: <MonitorPlay size={16} /> },
    { id: 'Tareas pendientes', label: 'Tareas', icon: <CheckSquare size={16} /> },
    { id: 'Mensajes programados', label: 'Programados', icon: <MessageSquare size={16} /> },
    { id: 'Mensajes fijados', label: 'Fijados', icon: <Pin size={16} /> }
  ];

  // Extract pinned messages
  const pinnedMessages = React.useMemo(() => {
    return messages?.filter(m => m.is_pinned) || [];
  }, [messages]);

  // Extract task IDs from messages
  const taskIds = React.useMemo(() => {
    const ids = new Set();
    const taskRegex = /colonny\.xyz\/(?!p\/)(?:t|([a-zA-Z0-9_-]+))\/([a-fA-F0-9-]{36})/;
    messages?.forEach(msg => {
      if (msg.text_content) {
        const match = msg.text_content.match(taskRegex);
        if (match && match[2]) ids.add(match[2]);
      }
    });
    return Array.from(ids);
  }, [messages]);

  return (
    <div className="user-profile-panel">
      {/* Header matching WhatsPanel */}
      <div className="up-header">
        <div className="up-header-top-row">
          <div className="up-header-top">
            <div className="up-avatar-wrapper" onClick={handleAvatarClick} title="Cambiar foto" style={{ width: "36px", height: "36px", opacity: uploadingAvatar ? 0.5 : 1 }}>
              <ChatAvatar
                url={contactData?.avatar_url || chat?.avatar_url}
                name={contactName}
                jid={chat?.chat_jid}
                isGroup={isGroupJid(chat?.chat_jid)}
              />
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
            <div className="up-info">
              <div className="up-name-row" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <EditableField value={contactData?.name || chat?.name} placeholder="Nombre del contacto" onSave={(val) => handleUpdateContact({ name: val }, true)} />
                {lastSeenText && (
                  <span style={{ fontSize: "12px", color: isConnectedText ? "#25D366" : "#667781", fontWeight: isConnectedText ? "500" : "normal" }}>
                    {lastSeenText}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="up-header-actions">
            {onClose && (
              <button className="up-close-btn" onClick={onClose} title="Cerrar panel">
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="up-tabs-container" style={{ padding: '0 var(--space-4)' }}>
          {tabs.map(tab => (
            <div 
              key={tab.id}
              className={`up-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              style={{ padding: "var(--space-2) var(--space-3)", gap: "6px" }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="up-content" style={{ padding: 'var(--space-4)' }}>
        {activeTab === 'Settings' && (
          <>
            {/* Contact Details Box */}
            <div className="up-contact-box">
              <div className="up-contact-row">
                <div className="up-contact-value">
                  <Phone size={14} color="#888" />
                  <button className="up-copy-btn" onClick={handleCopyPhone} title="Copiar número">
                    <Copy size={12} />
                  </button>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#111' }}>{formattedNumber}</span>
                </div>
                
                <div className="up-contact-value">
                  <Mail size={14} color="#888" />
                  <EditableField value={emailAddr} placeholder="No tiene correo, click para agregar" onSave={(val) => handleUpdateContact({ email: val })} type="email" />
                </div>
                
                <div className="up-contact-value">
                  <Briefcase size={14} color="#888" />
                  <EditableField value={companyName} placeholder="No tiene empresa, click para agregar" onSave={(val) => handleUpdateContact({ company: val })} />
                </div>
              </div>
              
              <div className="up-tags-header">
                {tags.map((tag, idx) => (
                  <span key={idx} className="up-tag-gray">{tag}</span>
                ))}
                <button className="up-add-tag-btn" onClick={handleAddTag}>
                  <Plus size={12} />
                </button>
              </div>

              {/* Integración main→dev (catch-up F): vincular este chat con otro en la bandeja. */}
              {!isGroup && (
                <button type="button" className="up-link-chat-btn" onClick={() => setIsLinkModalOpen(true)}>
                  <MessageCircle size={13} /> Vincular con otro chat
                </button>
              )}
            </div>

            {/* Stat Cards Grid (4 columns) */}
            <div className="up-summary-grid">
              <div className="up-stat-card">
                <div className="up-stat-top">
                  <div className="up-stat-icon-wrapper red">
                    <ClipboardList />
                  </div>
                  <div className="up-stat-content">
                    <div className="up-stat-number">2</div>
                    <div className="up-stat-title">Pendientes</div>
                  </div>
                </div>
                <div className="up-stat-tag red">⚠️ Requiere atención</div>
              </div>
              
              <div className="up-stat-card">
                <div className="up-stat-top">
                  <div className="up-stat-icon-wrapper blue">
                    <CalendarClock />
                  </div>
                  <div className="up-stat-content">
                    <div className="up-stat-number">1</div>
                    <div className="up-stat-title">Programado</div>
                  </div>
                </div>
                <div className="up-stat-tag blue">Mañana 9:00 a.m.</div>
              </div>
              
              <div className="up-stat-card">
                <div className="up-stat-top">
                  <div className="up-stat-icon-wrapper indigo">
                    <MessageCircle />
                  </div>
                  <div className="up-stat-content">
                    <div className="up-stat-number">{messages?.filter(m => m.from_me)?.length || 0}</div>
                    <div className="up-stat-title">Mensajes</div>
                  </div>
                </div>
                <div className="up-stat-tag indigo">Enviados</div>
              </div>
              
              <div className="up-stat-card">
                <div className="up-stat-top">
                  <div className="up-stat-icon-wrapper green">
                    <Heart />
                  </div>
                  <div className="up-stat-content">
                    <div className="up-stat-number" style={{ fontSize: '13px', paddingTop: '2px' }}>Prioridad</div>
                    <div className="up-stat-title" style={{ opacity: 0 }}>-</div>
                  </div>
                </div>
                <div className="up-priority-selector">
                  <span onClick={() => handlePriorityChange('baja')} className={`up-priority-option ${currentPriority === 'baja' ? 'active-baja' : ''}`}>Baja</span>
                  <span onClick={() => handlePriorityChange('alta')} className={`up-priority-option ${currentPriority === 'alta' ? 'active-alta' : ''}`}>Alta</span>
                  <span onClick={() => handlePriorityChange('urgente')} className={`up-priority-option ${currentPriority === 'urgente' ? 'active-urgente' : ''}`}>Urgente</span>
                </div>
              </div>
            </div>
            
            {/* Tareas Pendientes List (Limitar a 4) */}
            <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>Tareas pendientes <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginLeft: '6px' }}>2</span></h3>
                <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#10b981', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '12px', fontWeight: '500' }}>
                  <Plus size={14} /> Nueva
                </button>
              </div>
              <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <input type="checkbox" style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#10b981' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#111' }}>Enviar propuesta comercial</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Hoy • Asignada a ti <span style={{ color: '#ef4444', marginLeft: '6px' }}>Alta</span></div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <input type="checkbox" style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#10b981' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#111' }}>Confirmar documentación firmada</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>28 Ago • Asignada a ti <span style={{ color: '#f59e0b', marginLeft: '6px' }}>Media</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ height: '16px' }}></div>

            {/* Documentos y fotos enviados */}
            <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FolderOpen size={16} color="#00a884" />
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>
                    Documentos y fotos
                  </h3>
                  <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                    {mediaStats.totalCount || allMediaList.length}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Tag Verde Fotos con Icono de Descargar */}
                  <button
                    onClick={() => setMediaFilter('photos')}
                    style={{
                      background: mediaFilter === 'photos' ? '#dcfce7' : 'transparent',
                      color: mediaFilter === 'photos' ? '#15803d' : '#64748b',
                      border: mediaFilter === 'photos' ? '1px solid #86efac' : '1px solid transparent',
                      borderRadius: '8px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <ImageIcon size={12} color={mediaFilter === 'photos' ? '#16a34a' : '#64748b'} />
                    <span>Fotos ({mediaStats.imagesCount + mediaStats.videosCount})</span>
                    {(mediaStats.imagesCount + mediaStats.videosCount) > 0 && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadZip('photos');
                        }}
                        title="Descargar todas las fotos y videos (.zip)"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          backgroundColor: mediaFilter === 'photos' ? '#16a34a' : '#e2e8f0',
                          color: mediaFilter === 'photos' ? '#ffffff' : '#475569',
                          marginLeft: '2px',
                          cursor: 'pointer',
                          transition: 'transform 0.15s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <Download size={10} />
                      </span>
                    )}
                  </button>

                  {/* Tag Docs con Icono de Descargar si docs > 1 */}
                  <button
                    onClick={() => setMediaFilter('docs')}
                    style={{
                      background: mediaFilter === 'docs' ? '#e0f2fe' : 'transparent',
                      color: mediaFilter === 'docs' ? '#0369a1' : '#64748b',
                      border: mediaFilter === 'docs' ? '1px solid #bae6fd' : '1px solid transparent',
                      borderRadius: '8px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <FileText size={12} color={mediaFilter === 'docs' ? '#0284c7' : '#64748b'} />
                    <span>Docs ({mediaStats.docsCount})</span>
                    {mediaStats.docsCount > 1 && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadZip('docs');
                        }}
                        title="Descargar todos los documentos (.zip)"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          backgroundColor: mediaFilter === 'docs' ? '#0284c7' : '#e2e8f0',
                          color: mediaFilter === 'docs' ? '#ffffff' : '#475569',
                          marginLeft: '2px',
                          cursor: 'pointer',
                          transition: 'transform 0.15s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <Download size={10} />
                      </span>
                    )}
                  </button>
                </div>
              </div>

              <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
                {chatMediaQuery.isLoading ? (
                  <div style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', fontSize: '12px' }}>
                    Cargando archivos...
                  </div>
                ) : displayedMedia.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px 8px', color: '#94a3b8', fontSize: '12.5px' }}>
                    No hay {mediaFilter === 'photos' ? 'fotos o videos' : mediaFilter === 'docs' ? 'documentos' : 'archivos'} compartidos en este chat.
                  </div>
                ) : mediaFilter === 'photos' ? (
                  /* Grid de Fotos y Videos con botón Ver todas */
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(68px, 1fr))', gap: '6px' }}>
                      {displayedMedia.slice(0, 12).map((item) => {
                        const isVid = item.media_type === 'video' || item.media_type === 'videoMessage' || item.media_url?.endsWith('.mp4');
                        return (
                          <div
                            key={item.id || item.msg_id}
                            onClick={() => setPreviewMedia({ url: item.media_url, name: item.text_content || 'Foto', isVideo: isVid })}
                            style={{
                              position: 'relative',
                              aspectRatio: '1',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              backgroundColor: '#0f172a',
                              border: '1px solid rgba(0,0,0,0.06)',
                              transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.04)';
                              e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.12)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            title={item.text_content || 'Ver multimedia'}
                          >
                            {isVid ? (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e293b' }}>
                                <Video size={22} color="#fff" />
                                <span style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '9px', padding: '1px 4px', borderRadius: '4px' }}>VIDEO</span>
                              </div>
                            ) : (
                              <img
                                src={item.media_url}
                                alt={item.text_content || 'Foto'}
                                loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Botón Ver todas las fotos */}
                    <button
                      onClick={() => setShowGalleryModal(true)}
                      style={{
                        marginTop: '10px',
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        color: '#0f172a',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e2e8f0';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <Eye size={13} />
                      <span>Ver todas ({mediaStats.imagesCount + mediaStats.videosCount})</span>
                    </button>
                  </div>
                ) : (
                  /* Lista de Documentos */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {displayedMedia.slice(0, 8).map((doc) => {
                      const docName = doc.text_content || getCleanFileName(doc.media_url, 'Documento adjunto');
                      const isPdf = docName.toLowerCase().endsWith('.pdf') || doc.media_url?.toLowerCase().includes('.pdf');
                      const isSheet = docName.toLowerCase().match(/\.(xls|xlsx|csv)/);
                      const isWord = docName.toLowerCase().match(/\.(doc|docx)/);
                      return (
                        <div
                          key={doc.id || doc.msg_id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 10px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            gap: '8px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                            <div style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isPdf ? '#fee2e2' : isSheet ? '#dcfce7' : isWord ? '#e0f2fe' : '#f1f5f9',
                              color: isPdf ? '#ef4444' : isSheet ? '#16a34a' : isWord ? '#2563eb' : '#64748b',
                              flexShrink: 0
                            }}>
                              <FileText size={16} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                              <span style={{ fontSize: '12.5px', fontWeight: '500', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {docName}
                              </span>
                              <span style={{ fontSize: '10.5px', color: '#64748b' }}>
                                {doc.timestamp ? new Date(doc.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                                {doc.from_me ? ' • Enviado por ti' : ' • Recibido'}
                              </span>
                            </div>
                          </div>
                          <a
                            href={doc.media_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            title="Descargar / Abrir"
                            style={{
                              padding: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#64748b',
                              borderRadius: '6px',
                              textDecoration: 'none'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#00a884'; e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <Download size={15} />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ height: '16px' }}></div>
            {/* Mensajes Programados List (Limitar a 4) */}
            <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>Mensajes programados <span style={{ background: '#e0f2fe', color: '#1d4ed8', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginLeft: '6px' }}>1</span></h3>
                <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '12px', fontWeight: '500' }}>
                  <Plus size={14} /> Programar
                </button>
              </div>
              <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                  <MessageSquare size={16} color="#3b82f6" style={{ marginTop: '2px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#111' }}>Mañana • 9:00 AM</div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: 'var(--space-1)', padding: 'var(--space-2)', background: '#f3f4f6', borderRadius: '6px' }}>Hola, te comparto la propuesta que revisamos ayer...</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ height: '16px' }}></div>

            {/* Grupos en común o Miembros (dentro de Settings) */}
            <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>
                    {isGroup ? 'Miembros del grupo' : 'Grupos en común'}
                  </h3>
                  {isGroup && (
                    <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                      {groupMembersQuery.data?.members?.length || 0}
                    </span>
                  )}
                </div>

                {!isGroup ? (
                  <button
                    onClick={() => setIsCreateGroupModalOpen(true)}
                    style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      color: '#16a34a',
                      borderRadius: '8px',
                      padding: '4px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '12px',
                      fontWeight: '600',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dcfce7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                  >
                    <Plus size={14} />
                    <span>Crear grupo</span>
                  </button>
                ) : groupMembersQuery.data?.isCurrentUserAdmin ? (
                  <button
                    onClick={() => setIsAddParticipantModalOpen(true)}
                    style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      color: '#16a34a',
                      borderRadius: '8px',
                      padding: '4px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '12px',
                      fontWeight: '600',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dcfce7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                  >
                    <UserPlus size={14} />
                    <span>Añadir</span>
                  </button>
                ) : null}
              </div>

              {isGroup ? (
                groupMembersQuery.isLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Cargando miembros...</div>
                ) : groupMembersQuery.isError ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>Error al cargar miembros.</div>
                ) : groupMembersQuery.data?.members?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {groupMembersQuery.data.members.map(member => {
                      const localChat = allChats.find(c => c.chat_jid === member.jid) || { chat_jid: member.jid, name: formatWhatsAppNumber(member.jid) };
                      const myPhone = groupMembersQuery.data?.currentUserJid ? groupMembersQuery.data.currentUserJid.split(':')[0].split('@')[0] : '';
                      const memberPhone = member.jid ? member.jid.split(':')[0].split('@')[0] : '';
                      const isCurrentLoggedUser = Boolean(myPhone && memberPhone === myPhone);

                      return (
                        <div 
                          key={member.jid} 
                          style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', borderBottom: '1px solid #f0f0f0', transition: 'background-color 0.15s ease' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, cursor: 'pointer' }}
                            onClick={() => onSelectChat && onSelectChat(localChat)}
                          >
                            <div style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                              <ChatAvatar url={localChat?.avatar_url} name={localChat.name} jid={member.jid} isGroup={false} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13.5px', fontWeight: '500', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {localChat.name} {isCurrentLoggedUser && <span style={{ color: '#64748b', fontSize: '11.5px' }}>(Tú)</span>}
                              </div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>
                                {formatWhatsAppNumber(member.jid)}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            {member.admin && (
                              <span style={{ fontSize: '10.5px', color: '#15803d', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                                {member.admin === 'superadmin' ? 'Creador' : 'Admin'}
                              </span>
                            )}
                            {groupMembersQuery.data?.isCurrentUserAdmin && !isCurrentLoggedUser && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMemberToRemove({ member, name: localChat.name || formatWhatsAppNumber(member.jid) });
                                }}
                                title="Eliminar del grupo"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#94a3b8',
                                  padding: '6px',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#ef4444';
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#94a3b8';
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <UserMinus size={15} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No hay miembros.</div>
                )
              ) : (
                commonGroupsQuery.isLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Cargando grupos...</div>
                ) : commonGroupsQuery.isError ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>Error al cargar grupos.</div>
                ) : commonGroupsQuery.data?.groups?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {commonGroupsQuery.data.groups.map(group => {
                      const localChat = allChats.find(c => c.chat_jid === group.jid) || { chat_jid: group.jid, name: group.subject };
                      return (
                        <div 
                          key={group.jid} 
                          style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                          onClick={() => onSelectChat && onSelectChat(localChat)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ width: '40px', height: '40px' }}>
                            <ChatAvatar url={localChat?.avatar_url} name={group.subject} jid={group.jid} isGroup={true} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{group.subject}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No hay grupos en común.</div>
                )
              )}
            </div>

            {/* Add space at bottom */}
            <div style={{ height: '24px' }}></div>
          </>
        )}
        
        {activeTab === 'Tareas pendientes' && (
          <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>Tareas del chat <span style={{ background: '#f3f4f6', color: '#4b5563', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginLeft: '6px' }}>{taskIds.length}</span></h3>
              <button 
                onClick={() => setIsTaskSearchOpen(!isTaskSearchOpen)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#10b981', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '12px', fontWeight: '500' }}
              >
                <Plus size={14} /> Vincular
              </button>
            </div>
            {isTaskSearchOpen && (
              <div style={{ padding: '0 var(--space-4)', zIndex: 10 }}>
                <WhatsTaskLinkSearch 
                  chatJid={chat?.chat_jid} 
                  onClose={() => setIsTaskSearchOpen(false)}
                  onLinkComplete={() => setIsTaskSearchOpen(false)} 
                />
              </div>
            )}
            {taskIds.length > 0 ? (
              <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {taskIds.map(taskId => (
                  <WhatsTaskCard key={taskId} taskId={taskId} onClick={() => onOpenTask ? onOpenTask(taskId) : window.open(`https://colonny.xyz/t/${taskId}`, '_blank')} />
                ))}
              </div>
            ) : (
              <div style={{ padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckSquare size={20} color="#9ca3af" />
                </div>
                <span style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>No hay tareas vinculadas a este contacto.</span>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Mensajes programados' && (
          <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>Mensajes programados <span style={{ background: '#e0f2fe', color: '#1d4ed8', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginLeft: '6px' }}>1</span></h3>
              <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '12px', fontWeight: '500' }}>
                <Plus size={14} /> Programar
              </button>
            </div>
            <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                <MessageSquare size={16} color="#3b82f6" style={{ marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#111' }}>Mañana • 9:00 AM</div>
                  <div style={{ fontSize: '12px', color: '#555', marginTop: 'var(--space-1)', padding: 'var(--space-2)', background: '#f3f4f6', borderRadius: '6px' }}>Hola, te comparto la propuesta que revisamos ayer...</div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'Mensajes fijados' && (
          <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>
                Mensajes fijados <span style={{ background: '#e0f2fe', color: '#1d4ed8', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginLeft: '6px' }}>{pinnedMessages.length}</span>
              </h3>
            </div>
            {pinnedMessages.length > 0 ? (
              <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {pinnedMessages.map((msg) => (
                  <div 
                    key={msg.msg_id} 
                    onClick={() => {
                      const el = document.getElementById(`whats-msg-${msg.msg_id}`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 'var(--space-3)', 
                      padding: 'var(--space-2) var(--space-3)', 
                      background: '#f8fafc', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer' 
                    }}
                  >
                    <Pin size={16} color="#00a884" style={{ marginTop: '3px', flexShrink: 0, transform: 'rotate(45deg)' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </div>
                      <div style={{ fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.text_content || 'Archivo multimedia'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pin size={20} color="#9ca3af" />
                </div>
                <span style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>No hay mensajes fijados en este chat.</span>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Sessions' && (
          <div className="up-contact-box" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>Sesiones <span style={{ background: '#f3f4f6', color: '#4b5563', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginLeft: '6px' }}>Activas</span></h3>
            </div>
            <div style={{ padding: 'var(--space-4)' }}>
              <ComponentUserSessions userId={contactData?.user_id} jid={chat?.chat_jid} theme="light" />
            </div>
          </div>
        )}
      </div>

      {isLinkModalOpen && (
        <WhatsLinkChatModal
          currentJid={chat?.chat_jid}
          allChats={allChats}
          isSubmitting={linkChatMutation.isPending}
          onClose={() => setIsLinkModalOpen(false)}
          onSelect={(parentJid) => {
            linkChatMutation.mutate(
              { childJid: chat.chat_jid, parentJid },
              { onSuccess: () => setIsLinkModalOpen(false) },
            );
          }}
        />
      )}
            {/* Modal Galería Completa de Fotos y Videos */}
      {showGalleryModal && (
        <div
          onClick={() => setShowGalleryModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(4px)',
            zIndex: 99998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '820px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Cabecera Galería */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#f8fafc'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ImageIcon size={18} color="#16a34a" />
                  <span>Galería de fotos y videos</span>
                  <span style={{ fontSize: '12px', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '12px', fontWeight: '600' }}>
                    {mediaStats.imagesCount + mediaStats.videosCount}
                  </span>
                </h3>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  {chat?.name || 'Chat'}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Botón Descargar Todo */}
                <button
                  onClick={() => handleDownloadZip('photos')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '7px 12px',
                    backgroundColor: '#16a34a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#16a34a'; }}
                >
                  <Download size={14} />
                  <span>Descargar todo (.zip)</span>
                </button>

                {/* Botón Cerrar */}
                <button
                  onClick={() => setShowGalleryModal(false)}
                  style={{
                    padding: '6px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#e2e8f0',
                    color: '#475569',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Sub-filtros dentro de la galería */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setGalleryFilter('all')}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: galleryFilter === 'all' ? '#0f172a' : '#f1f5f9',
                  color: galleryFilter === 'all' ? '#ffffff' : '#64748b'
                }}
              >
                Todas ({photoMedia.length})
              </button>
              <button
                onClick={() => setGalleryFilter('photos')}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: galleryFilter === 'photos' ? '#16a34a' : '#f1f5f9',
                  color: galleryFilter === 'photos' ? '#ffffff' : '#64748b'
                }}
              >
                Fotos ({mediaStats.imagesCount})
              </button>
              <button
                onClick={() => setGalleryFilter('videos')}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: galleryFilter === 'videos' ? '#3b82f6' : '#f1f5f9',
                  color: galleryFilter === 'videos' ? '#ffffff' : '#64748b'
                }}
              >
                Videos ({mediaStats.videosCount})
              </button>
            </div>

            {/* Cuadrícula de Galería */}
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {(() => {
                const itemsToRender = galleryFilter === 'photos'
                  ? photoMedia.filter(m => m.media_type === 'image' || m.media_type === 'imageMessage' || !m.media_url?.endsWith('.mp4'))
                  : galleryFilter === 'videos'
                  ? photoMedia.filter(m => m.media_type === 'video' || m.media_type === 'videoMessage' || m.media_url?.endsWith('.mp4'))
                  : photoMedia;

                if (itemsToRender.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>
                      No se encontraron elementos multimedia en esta categoría.
                    </div>
                  );
                }

                return (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                    gap: '10px'
                  }}>
                    {itemsToRender.map((item) => {
                      const isVid = item.media_type === 'video' || item.media_type === 'videoMessage' || item.media_url?.endsWith('.mp4');
                      return (
                        <div
                          key={item.id || item.msg_id}
                          onClick={() => setPreviewMedia({ url: item.media_url, name: item.text_content || 'Foto', isVideo: isVid })}
                          style={{
                            position: 'relative',
                            aspectRatio: '1',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            backgroundColor: '#0f172a',
                            border: '1px solid rgba(0,0,0,0.06)',
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.18)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {isVid ? (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e293b' }}>
                              <Video size={28} color="#ffffff" />
                              <span style={{ position: 'absolute', bottom: '6px', right: '6px', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '10px', padding: '2px 5px', borderRadius: '4px', fontWeight: '600' }}>
                                VIDEO
                              </span>
                            </div>
                          ) : (
                            <img
                              src={item.media_url}
                              alt={item.text_content || 'Foto'}
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                            padding: '12px 6px 4px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: '#fff',
                            fontSize: '9.5px',
                            opacity: 0.9
                          }}>
                            <span>{item.timestamp ? new Date(item.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}</span>
                            <Eye size={11} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Grupo */}
      {isCreateGroupModalOpen && (
        <WhatsCreateGroupModal
          currentChat={chat}
          allChats={allChats}
          onClose={() => setIsCreateGroupModalOpen(false)}
          onCreateGroup={handleCreateGroup}
        />
      )}

      {/* Modal Añadir Participantes a Grupo */}
      {isAddParticipantModalOpen && (
        <WhatsAddParticipantsModal
          groupSubject={chat?.name}
          existingMemberJids={(groupMembersQuery.data?.members || []).map(m => m.jid)}
          allChats={allChats}
          onClose={() => setIsAddParticipantModalOpen(false)}
          onAddParticipants={handleAddParticipants}
        />
      )}

      {/* Modal Confirmar Eliminar Participante */}
      {memberToRemove && (
        <div
          className="whats-link-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setMemberToRemove(null); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(3px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '380px',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UserMinus size={18} />
              </div>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                Eliminar del grupo
              </h4>
            </div>

            <p style={{ margin: '0 0 20px 0', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
              ¿Estás seguro de que deseas eliminar a <strong>{memberToRemove.name}</strong> de este grupo?
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setMemberToRemove(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#fff',
                  color: '#475569',
                  fontSize: '12.5px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRemoveMember}
                style={{
                  padding: '8px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  fontSize: '12.5px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.25)'
                }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Lightbox de Preview de Fotos / Multimedia */}
      {previewMedia && (
        <div
          onClick={() => setPreviewMedia(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            backdropFilter: 'blur(5px)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', color: '#fff' }}>
              <span style={{ fontSize: '13px', fontWeight: '500', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {previewMedia.name}
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <a
                  href={previewMedia.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#fff', background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Download size={14} /> Descargar
                </a>
                <button
                  onClick={() => setPreviewMedia(null)}
                  style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {previewMedia.isVideo ? (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
              />
            ) : (
              <img
                src={previewMedia.url}
                alt={previewMedia.name}
                style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfilePanel;
